import { useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'
import type { CalendarFilter, CalendarFilterOptions } from '../api/calendar'
import { useCan } from '../useSession'
import { filterIsActive } from '../api/calendar'

type Props = {
  options: CalendarFilterOptions | undefined
  filter: CalendarFilter
  onChange: (next: CalendarFilter) => void
  matched: number
  total: number
}

/**
 * One filter, behind a button that names it (D-582).
 *
 * <b>Every value of every filter used to be on screen at once.</b> Two
 * programmes, six rooms, <b>seventeen teachers</b> and ten styles - thirty-five
 * chips in four labelled rows, above a week that needs the height more. The
 * teacher row is the one that grows every time somebody is hired.
 *
 * <b>The name stays visible; the values go behind it.</b> That is the whole
 * difference from hiding the filters altogether, which saves more height and
 * asks the reader to already know they exist - wrong for a screen a new teacher
 * opens once a term.
 *
 * <b>Still single-select, and still clears by re-picking</b> (the behaviour
 * <c>ChipGroup</c> had): choosing what is already chosen is faster than hunting
 * for an "all" option, and it is the only way to clear one filter without
 * clearing the rest.
 *
 * <b>A finder appears past eight values</b>, which today catches <i>Teacher</i>
 * (16) and <i>Style</i> (10) and leaves Program (2) and Room (6) alone.
 * <c>observed</c> from the live studio's own filter options - an earlier note
 * here said Teacher only, which was wrong about the screen it describes.
 */
/** The panel's own width, in px, so the overflow check and the class agree. */
const POPOVER_WIDTH = 240

function FilterMenu({
  label,
  values,
  selected,
  onSelect,
}: {
  label: string
  values: string[]
  selected: string | undefined
  onSelect: (value: string | undefined) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const holder = useRef<HTMLDivElement>(null)
  const finderBox = useRef<HTMLInputElement>(null)

  /*
   * <b>Which edge to hang it from</b> (D-584).
   *
   * A fixed-width panel hung from the left of its button leaves the viewport
   * the moment the button is far enough across. <c>observed</c> on a 390px
   * phone: the Teacher trigger sits at x=221, so a 240px panel wanted 461px
   * and the document <b>gained 71px of sideways scroll</b> - the metric
   * <c>audit.mjs</c> exists to catch (D-229, D-255), on the one filter this
   * whole change is about.
   *
   * Measured on opening rather than guessed from a breakpoint: what matters is
   * where <i>this</i> button ended up after the row wrapped, which no media
   * query knows.
   */
  const [fromRight, setFromRight] = useState(false)

  /*
   * Closing on a click anywhere else, and on Escape. Both are listeners rather
   * than a blur handler: a blur fires before the click it caused, so choosing a
   * value would close the menu and never reach the option.
   */
  useEffect(() => {
    if (!open) return

    const box = holder.current?.getBoundingClientRect()
    if (box) setFromRight(box.left + POPOVER_WIDTH > window.innerWidth - 8)

    /*
     * <b>Focused without letting the browser scroll to it</b> (D-584).
     * `autoFocus` scrolls a focused input into view, which on a phone dragged
     * the page 62px right and left it there - the rail, the heading and the
     * left edge of every card off-screen. The focus is worth keeping; the
     * scrolling is not.
     */
    finderBox.current?.focus({ preventScroll: true })

    const away = (e: MouseEvent) => {
      if (!holder.current?.contains(e.target as Node)) setOpen(false)
    }
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }

    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', key)

    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', key)
    }
  }, [open])

  if (values.length <= 1) return null

  const finder = values.length > 8
  const shown = query
    ? values.filter((v) => v.toLowerCase().includes(query.toLowerCase()))
    : values

  const choose = (value: string) => {
    onSelect(value === selected ? undefined : value)
    setOpen(false)
    setQuery('')
  }

  return (
    <div ref={holder} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        className={`flex items-center gap-1.5 rounded-lg border px-3 py-2 text-[12.5px] transition-colors ${
          selected
            ? 'border-accent bg-accent-soft font-semibold text-accent-2'
            : 'border-line-2 bg-surface text-ink-2 hover:border-ink-3'
        }`}
      >
        {/* The label always, so the row still says what can be filtered. */}
        {selected ? `${label}: ${selected}` : label}
        <ChevronDown size={13} strokeWidth={2.5} aria-hidden="true" />
      </button>

      {open && (
        <div
          className={`absolute z-30 mt-1 max-h-[17rem] w-[15rem] max-w-[calc(100vw-1.5rem)] overflow-y-auto rounded-xl border border-line bg-surface p-2 shadow-lg ${
            fromRight ? 'right-0' : 'left-0'
          }`}
        >
          {finder && (
            <input
              ref={finderBox}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Find a ${label.toLowerCase()}`}
              className="mb-1.5 w-full rounded-lg border border-line-2 bg-surface-2 px-2.5 py-1.5 text-[12.5px] text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none"
            />
          )}

          <div role="listbox" aria-label={label}>
            {shown.map((value) => (
              <button
                key={value}
                type="button"
                role="option"
                aria-selected={value === selected}
                onClick={() => choose(value)}
                className={`block w-full rounded-lg px-2.5 py-1.5 text-left text-[12.5px] hover:bg-surface-2 ${
                  value === selected ? 'font-semibold text-accent-2' : 'text-ink-2'
                }`}
              >
                {value}
              </button>
            ))}

            {/*
              Never an empty popover: a finder that matches nothing looks
              broken, and the reader cannot tell a typo from a teacher who does
              not teach this week.
            */}
            {shown.length === 0 && (
              <p className="px-2.5 py-1.5 text-[12.5px] text-ink-3">
                No {label.toLowerCase()} matches &ldquo;{query}&rdquo;.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default function CalendarFilters({ options, filter, onChange, matched, total }: Props) {
  // Read here rather than passed down: every caller would compute the same
  // thing, and a prop nobody can get wrong is one that does not exist.
  const seesEverything = useCan('ViewAllSchedules')

  // Privates are bookings, and only somebody who may hold a room has any
  // (D-649). A parent's calendar carries none, so the toggle would describe a
  // state she cannot reach.
  const canBookRooms = useCan('BookRooms')

  const active = filterIsActive(filter)
  const set = (patch: Partial<CalendarFilter>) => onChange({ ...filter, ...patch })

  return (
    <div className="mb-5 rounded-xl border border-line bg-surface p-3.5">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={filter.q ?? ''}
          onChange={(e) => set({ q: e.target.value })}
          placeholder="Search classes, teachers, rooms…"
          /*
            Narrower than it was (D-321). At flex-1 it ate the row and pushed
            every control to a second line; the studio toggle now shares that
            line, and a search box does not need to be the widest thing on the
            screen to be findable.
          */
          className="min-w-[200px] flex-1 rounded-lg border border-line-2 bg-surface-2 px-3 py-2 text-[13px] text-ink placeholder:text-ink-3 focus:border-accent focus:outline-none"
        />

        {/*
          "Show the whole studio", with the other filters rather than above them
          (D-321, amending D-299/D-300).
 
          It sat in its own bordered box over the calendar, which read as a
          banner and looked bolted on — reported twice. It is a filter: it widens
          what the view shows, exactly like Hide cancelled narrows it, so it
          belongs in the row where filters live.

          <b>Offered only to somebody the calendar is narrowing.</b> An account
          holding ViewAllSchedules already sees everything, so the control would
          describe a state the reader is not in — which is worse than no control
          (D-300). That also keeps it off the studio calendar screens, which are
          gated on that same capability.
        */}
        {!seesEverything && (
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-line-2 bg-surface px-3 py-2 text-[12.5px] text-ink-2">
            <input
              type="checkbox"
              checked={filter.wholeStudio ?? false}
              onChange={(e) => set({ wholeStudio: e.target.checked })}
              className="size-4 shrink-0 accent-[var(--color-accent)]"
            />
            Show studio schedule
          </label>
        )}

        {/*
          <b>Only for somebody who has privates</b> (D-649). `asked`, from the
          pilot: *"Teachers want a way in their calendar to quickly see just the
          privates they are scheduled for."* A parent's calendar holds her
          dancers' classes and no bookings at all, so the control would describe
          a state she cannot be in - the same reason the studio-schedule switch
          above is hidden from the people it cannot help (D-300).
        */}
        {canBookRooms && (
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-line-2 bg-surface px-3 py-2 text-[12.5px] text-ink-2">
            <input
              type="checkbox"
              checked={filter.privatesOnly ?? false}
              onChange={(e) => set({ privatesOnly: e.target.checked })}
              className="size-4 shrink-0 accent-[var(--color-accent)]"
            />
            Privates only
          </label>
        )}

        <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-line-2 bg-surface px-3 py-2 text-[12.5px] text-ink-2">
          <input
            type="checkbox"
            checked={filter.hideCancelled ?? false}
            onChange={(e) => set({ hideCancelled: e.target.checked })}
            /* Sized, or the browser default is a 13px box to hit (D-229). */
            className="size-4 shrink-0 accent-[var(--color-accent)]"
          />
          Hide cancelled
        </label>

        {active && (
          <button
            onClick={() => onChange({})}
            className="rounded-lg border border-line-2 bg-surface px-3 py-2 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
          >
            Clear
          </button>
        )}
      </div>

      {/*
        <b>One row, not four</b> (D-582). Wrapping rather than scrolling, so a
        narrow window gets two short rows instead of a horizontal scroller
        nobody discovers.
      */}
      <div className="flex flex-wrap items-center gap-2">
        <FilterMenu
          label="Program"
          values={options?.programs ?? []}
          selected={filter.program}
          onSelect={(v) => set({ program: v })}
        />
        <FilterMenu
          label="Room"
          values={options?.rooms ?? []}
          selected={filter.room}
          onSelect={(v) => set({ room: v })}
        />
        <FilterMenu
          label="Teacher"
          values={options?.instructors ?? []}
          selected={filter.instructor}
          onSelect={(v) => set({ instructor: v })}
        />
        <FilterMenu
          label="Style"
          values={options?.styles ?? []}
          selected={filter.style}
          onSelect={(v) => set({ style: v })}
        />
      </div>

      {active && (
        <p className="mt-3 border-t border-line pt-2.5 text-[12.5px] text-ink-3">
          {matched === 0 ? (
            <span className="text-warn">
              Nothing matches these filters this week. Try clearing one.
            </span>
          ) : (
            <>
              Showing <b className="text-ink">{matched}</b> of {total} sessions
            </>
          )}
        </p>
      )}
    </div>
  )
}
